import { uniqueId } from 'lodash';

import type { Toast } from '@app/components/toasts/types';

type SetToasts = (cb: (toasts: Toast[]) => Toast[]) => void;

type ObserverId = string;

class ToastObserver {
  static toastObservers: Record<string, ToastObserver | null> = {};

  static queue: Record<ObserverId, Toast[]> = {};

  private setToasts: SetToasts;
  private observerId: string;

  constructor(observerId: ObserverId, setToasts: SetToasts) {
    this.setToasts = setToasts;
    this.observerId = observerId;
  }

  public removeReoccurred(id: Toast['id']) {
    this.setToasts?.((toasts: Toast[]) => {
      const newToasts = toasts.map((toast) =>
        toast.id === id
          ? {
              ...toast,
              reoccurred: false,
            }
          : toast,
      );
      return newToasts;
    });
  }

  public create(toast: Toast): Toast['id'] {
    // Ensure that existing toasts rendered by *other* ToastObserver instances are cleared
    // before showing a new toast for this observer.
    this.clearOtherInstances();

    const id = toast.id || Number(uniqueId());
    const newToast: Toast = {
      ...toast,
      id,
    };
    this.setToasts?.((toasts: Toast[]) => {
      const toastExists = toasts.some((_toast) => _toast.id === id);
      if (toastExists) {
        return toasts.map((_toast) =>
          _toast.id === id
            ? {
                ...toast,
                reoccurred: true,
              }
            : _toast,
        );
      }
      return [
        ...toasts,
        newToast,
      ];
    });
    return id;
  }

  public remove(id: Toast['id']) {
    // We set the remove here, the component will remove itself after the animation
    this.setToasts?.((toasts: Toast[]) =>
      toasts.map((toast) =>
        toast.id !== id
          ? toast
          : {
              ...toast,
              remove: true,
            },
      ),
    );
  }

  public update(toast: Toast) {
    this.setToasts?.((toasts: Toast[]) =>
      toasts.map((_toast) =>
        _toast.id === toast.id
          ? {
              ..._toast,
              ...toast,
            }
          : _toast,
      ),
    );
  }

  /**
   * Clear (flag for removal) the toasts rendered by every other `ToastObserver` instance
   * except the current one.
   *
   * When multiple `ToastProvider` components are mounted at once they can overlap on the
   * screen. To avoid duplicate/stacked notifications we iterate over all registered
   * observers and invoke `clear()` on those that don't match the current `observerId`.
   */
  private clearOtherInstances() {
    ToastObserver.getAllInstances().forEach((instance) => {
      if (instance && instance.observerId !== this.observerId) {
        instance.clear();
      }
    });
  }

  /**
   * Flag all toasts managed by this observer for removal.
   *
   * The `ToastProvider` will interpret the flag and animate each toast
   * out of view before un-mounting it.
   *
   * This is mainly used internally when a toast gets created for another `ToastObserver` instance,
   * ensuring only one visible stack of toasts at a time.
   */
  clear() {
    this.setToasts?.((toasts: Toast[]) =>
      toasts.map((toast) => ({
        ...toast,
        remove: true,
      })),
    );
  }

  /**
   * Return an array containing all live `ToastObserver` instances.
   *
   * Helpful for broadcasting operations (e.g. clearing toasts from other instances than the current one).
   */
  static getAllInstances() {
    return Object.values(ToastObserver.toastObservers);
  }

  static getInstanceById(observerId: ObserverId) {
    return ToastObserver.toastObservers[observerId];
  }

  static createInstance(observerId: ObserverId, setToasts: SetToasts) {
    if (!ToastObserver.toastObservers[observerId]) {
      ToastObserver.toastObservers[observerId] = new ToastObserver(observerId, setToasts);
    }

    return ToastObserver.toastObservers[observerId];
  }

  static clearInstance(observerId: ObserverId) {
    ToastObserver.toastObservers[observerId] = null;
    delete ToastObserver.toastObservers[observerId];
  }

  /**
   * When a toast is created, But the provider is not mounted yet, the toast is queued
   * As soon as the provider is mounted, the queued toasts will be rendered and the queue will
   * be cleared
   */
  static queueToast(observerId: ObserverId, toast: Toast) {
    if (!ToastObserver.queue[observerId]) {
      ToastObserver.queue[observerId] = [];
    }

    ToastObserver.queue[observerId].push(toast);
  }

  static getQueuedToasts(observerId: ObserverId): Toast[] | null {
    if (!ToastObserver.queue[observerId]) {
      return null;
    }

    return ToastObserver.queue[observerId];
  }

  static clearQueue(observerId: ObserverId) {
    ToastObserver.queue[observerId] = [];
  }

  static flushQueuedToasts(observerId: ObserverId) {
    const toasts = ToastObserver.getQueuedToasts(observerId);

    if (!toasts) {
      return;
    }

    const instance = ToastObserver.getInstanceById(observerId);
    toasts.forEach((toast) => {
      if (instance) {
        instance.create(toast);
      }
    });

    this.clearQueue(observerId);
  }
}

export default ToastObserver;
